import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, Cpu, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import EngineRating from "@/components/EngineRating";
import SoundHistogram from "@/components/SoundHistogram";
import SoundTypeAnalysis from "@/components/SoundTypeAnalysis";
import WisedriveAnalysis from "@/components/WisedriveAnalysis";
import PredictiveChart from "@/components/PredictiveChart";
import ValidationCard from "@/components/ValidationCard";
import OBDInsightCard from "@/components/OBDInsightCard";
import ExpertRecommendation from "@/components/ExpertRecommendation";
import AdvancedDiagnostic from "@/components/AdvancedDiagnostic";
import AIDiagnosisSection from "@/components/AIDiagnosisSection";
import BankAnalysis from "@/components/BankAnalysis";
import InsuranceAnalysis from "@/components/InsuranceAnalysis";

export default function ResultsDashboard({ result }) {
  if (!result) return null;

  const handleDownloadPdf = () => {
    if (result?.id) {
      window.open(api.getSoundPdfUrl(result.id), '_blank');
    }
  };

  return (
    <div data-testid="results-dashboard">
      {/* Download PDF Button */}
      <div className="flex justify-end mb-4">
        <Button 
          onClick={handleDownloadPdf} 
          variant="outline" 
          size="sm"
          className="gap-2 font-bold uppercase tracking-wider text-xs border-slate-300 hover:bg-slate-50"
          data-testid="download-pdf-btn"
        >
          <Download size={14} />
          Download PDF Report
        </Button>
      </div>

      <Tabs defaultValue="summary" className="w-full">
        <TabsList className="bg-slate-100 rounded-sm h-12 mb-6 p-1 w-full grid grid-cols-2">
          <TabsTrigger 
            value="summary" 
            className="text-base font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm h-full"
            data-testid="tab-summary-report"
          >
            <FileText size={16} className="mr-2" /> Summary Report
          </TabsTrigger>
          <TabsTrigger 
            value="technical" 
            className="text-base font-bold uppercase tracking-wider rounded-sm data-[state=active]:bg-white data-[state=active]:shadow-sm h-full"
            data-testid="tab-technical-analysis"
          >
            <Cpu size={16} className="mr-2" /> Technical AI Analysis
          </TabsTrigger>
        </TabsList>

        {/* SUMMARY REPORT TAB */}
        <TabsContent value="summary" className="space-y-4">
          {/* Engine Health Rating + Sound Validation + OBD-II Analysis */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <EngineRating rating={result.engine_rating} />
            <ValidationCard validation={result.validation} />
            <OBDInsightCard
              obdData={result.obd_data}
              hasObd={result.has_obd_data}
            />
          </div>

          {/* Engine Sound Type Analysis */}
          <SoundTypeAnalysis data={result.sound_classification} />

          {/* Wisedrive Sound AI Engine Analysis */}
          <WisedriveAnalysis aiAnalysis={result.ai_analysis} />

          {/* Predictive Charts - WITH/WITHOUT Maintenance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <PredictiveChart
              title="WITH Regular Maintenance"
              data={result.predictive_data?.maintained}
              currentKm={result.predictive_data?.current_km}
              type="maintained"
            />
            <PredictiveChart
              title="WITHOUT Maintenance"
              data={result.predictive_data?.unmaintained}
              currentKm={result.predictive_data?.current_km}
              type="unmaintained"
            />
          </div>

          {/* Wisedrive Expert Assessment (Warranty + Repairs + Market Value) */}
          <ExpertRecommendation
            predictiveData={result.predictive_data}
            carDetails={result.car_details}
            engineRating={result.engine_rating}
          />

          {/* Bank / Lender Analysis Section */}
          <BankAnalysis data={result.financial_analysis?.bank_analysis} />

          {/* Insurance Company Analysis Section */}
          <InsuranceAnalysis data={result.financial_analysis?.insurance_analysis} />
        </TabsContent>

        {/* TECHNICAL AI ANALYSIS TAB */}
        <TabsContent value="technical" className="space-y-4">
          {/* Sound Analysis (Histogram, Waveform, Energy) */}
          <SoundHistogram 
            data={result.histogram_data} 
            histogramImage={result.histogram_image}
            waveformImage={result.waveform_image}
          />

          {/* Advanced Engine Diagnostic - ECG-like Spectrogram Analysis */}
          <AdvancedDiagnostic 
            spectrogramDiagnostic={result.spectrogram_diagnostic}
            spectrogramImage={result.diagnostic_spectrogram_image}
            hasTurbo={result.car_details?.has_turbo || false}
          />

          {/* AI Predictive Diagnosis - Separate Section */}
          <AIDiagnosisSection aiDiagnosis={result.ai_diagnosis} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
